
import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import {
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  Router,
  Event
} from '@angular/router';
import { GlobalStoreService } from './modules/global/services/global-store.service';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { GlobalService } from './modules/global/services/global.service';

// MSAL Angular + Browser
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';

import { BsModalService, BsModalRef, ModalDirective } from 'ngx-bootstrap/modal';

import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date = null;
  title = 'angular-idle-timeout';

  public modalRef: BsModalRef;
  bytes: number = 0;
  public isIframe: boolean;

  @ViewChild('childModal', { static: false }) childModal: ModalDirective;

  // for cleanup of subscriptions
  private readonly destroying$ = new Subject<void>();

  constructor(
    private idle: Idle,
    private keepalive: Keepalive,
    private router: Router,
    public store: GlobalStoreService,
    private globalService: GlobalService,
    private msalService: MsalService,
    private msalBroadcast: MsalBroadcastService
  ) {
    // Avoid reload during acquireTokenSilent() in hidden iframe
    this.isIframe = window !== window.parent && !window.opener;

    // Idle: 45 min idle (2700s), 15 min timeout warning (900s)
    idle.setIdle(2700);
    idle.setTimeout(900);
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleEnd.subscribe(() => this.reset());

    idle.onTimeout.subscribe(() => {
      this.logout();
      this.idleState = 'Timed out!';
      this.childModal.hide();
      this.timedOut = true;
    });

    idle.onIdleStart.subscribe(() => {
      this.idle.clearInterrupts();
      this.idleState = 'You will be logged out in 45 minutes.';
      this.childModal.show();
      this.blinkTab();
    });

    idle.onTimeoutWarning.subscribe((countdown) => {
      const minutes = Math.floor(countdown / 60);
      const seconds = countdown - minutes * 60;
      this.idleState = `You will be logged out in ${minutes}:${seconds} minutes.`;
    });

    // Keepalive every 15 seconds
    keepalive.interval(15);
    keepalive.onPing.subscribe(() => (this.lastPing = new Date()));

    // Router-driven idle control + global loader
    router.events.subscribe((routerEvent: Event) => {
      if (
        this.router.url.search(/error/) === -1 &&
        this.router.url.search(/unauthorized/) === -1 &&
        this.router.url.search(/notfound/) === -1
      ) {
        idle.watch();
      } else {
        idle.stop();
      }
      this.checkRouterEvent(routerEvent);
    });
  }


 ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  checkRouterEvent(routerEvent: Event): void {
    if (routerEvent instanceof NavigationStart) {
      this.store.loading = true;
    }
    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      this.store.loading = false;
    }
  }

  reset(): void {
    this.idle.watch();
    this.timedOut = false;
  }

  logout(): void {
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.childModal.hide();
    this.store.loading = true;

    this.globalService.logout().subscribe({
      complete: () => {
        this.store.loading = false;

        // Use MSAL's redirect logout for SPAs
        this.msalService.logoutRedirect({
          postLogoutRedirectUri: environment.postLogoutRedirectUri
        });

        // Clear app-specific caches if needed
        localStorage.clear();
        sessionStorage.clear();
      }
    });
  }

  stay(): void {
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.childModal.hide();
    this.reset();
  }

  blinkTab = (function () {
    const oldTitle = document.title;
    const msg = oldTitle;
    let timeoutId: any;

    const blink = function () {
      document.title =
        document.title === msg ? 'You will be logged out soon' : msg;
    };
    const clear = function () {
      clearInterval(timeoutId);
      document.title = oldTitle;
      window.onmousemove = null;
      timeoutId = null;
    };

    return function () {
      if (!timeoutId) {
        timeoutId = setInterval(blink, 1000);
        // window.onmousemove = clear;
        window.addEventListener('mousemove', clear, { once: true });
      }
    };
  })();
}
